'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Article {
  id: number;
  title: string;
  excerpt: string;
  category: string;
  readTime: string;
  helpful: number;
  tags: string[];
}

interface KnowledgeBaseProps {
  className?: string;
}

export default function KnowledgeBase({ className = '' }: KnowledgeBaseProps) {
  const [selectedTag, setSelectedTag] = useState<string>('');

  const articles: Article[] = [
    {
      id: 1,
      title: 'Complete Guide to ParkPing Card Activation',
      excerpt:
        'Step-by-step instructions for activating your ParkPing card, linking your phone number, and setting up your account preferences.',
      category: 'Getting Started',
      readTime: '5 min read',
      helpful: 234,
      tags: ['activation', 'setup', 'beginner'],
    },
    {
      id: 2,
      title: 'Understanding Call Masking Technology',
      excerpt:
        'Learn how ParkPing protects your privacy through advanced call masking technology and secure communication protocols.',
      category: 'Privacy & Security',
      readTime: '4 min read',
      helpful: 189,
      tags: ['privacy', 'technology', 'security'],
    },
    {
      id: 3,
      title: 'Bulk Order Setup for Housing Societies',
      excerpt:
        'Comprehensive guide for society administrators to implement ParkPing across multiple units with centralized management.',
      category: 'Enterprise',
      readTime: '8 min read',
      helpful: 156,
      tags: ['bulk-order', 'society', 'enterprise'],
    },
    {
      id: 4,
      title: 'Troubleshooting Common Issues',
      excerpt:
        'Quick solutions for the most frequently encountered problems with ParkPing cards and the mobile app.',
      category: 'Support',
      readTime: '6 min read',
      helpful: 312,
      tags: ['troubleshooting', 'help', 'fixes'],
    },
    {
      id: 5,
      title: 'Managing Multiple Cards in One Account',
      excerpt:
        'How to add, organize, and manage multiple ParkPing cards for different vehicles under a single account.',
      category: 'Account Management',
      readTime: '3 min read',
      helpful: 145,
      tags: ['account', 'multiple-cards', 'management'],
    },
    {
      id: 6,
      title: 'Payment Methods and Billing',
      excerpt:
        'Everything you need to know about payment options, billing cycles, and managing your payment information.',
      category: 'Billing',
      readTime: '4 min read',
      helpful: 98,
      tags: ['payment', 'billing', 'finance'],
    },
  ];

  const allTags = Array.from(
    new Set(articles.flatMap((article) => article.tags))
  );

  const filteredArticles = selectedTag
    ? articles.filter((article) => article.tags.includes(selectedTag))
    : articles;

  return (
    <section
      id="getting-started"
      className={`py-16 bg-white ${className}`}
    >
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Knowledge Base
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Comprehensive guides and articles to help you get the most out of
              ParkPing
            </p>
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-8 justify-center">
            <button
              onClick={() => setSelectedTag('')}
              className={`px-3 py-1 text-sm rounded-full transition ${
                selectedTag === ''
                  ? 'bg-[#42AF47] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-[#42AF47]/10 hover:text-[#42AF47]'
              }`}
            >
              All Articles
            </button>

            {allTags.map((tag) => (
              <button
                key={tag}
                onClick={() => setSelectedTag(tag)}
                className={`px-3 py-1 text-sm rounded-full transition ${
                  selectedTag === tag
                    ? 'bg-[#42AF47] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-[#42AF47]/10 hover:text-[#42AF47]'
                }`}
              >
                #{tag}
              </button>
            ))}
          </div>

          {/* Articles */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredArticles.map((article) => (
              <div
                key={article.id}
                className="bg-white border border-gray-200 rounded-lg p-6 hover:border-[#42AF47]/40 hover:shadow-md transition cursor-pointer group"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs px-2 py-1 bg-[#42AF47]/10 text-[#42AF47] rounded">
                    {article.category}
                  </span>
                  <span className="text-xs text-gray-500">
                    {article.readTime}
                  </span>
                </div>

                <h3 className="text-lg font-semibold text-gray-900 mb-3 group-hover:text-[#42AF47] transition">
                  {article.title}
                </h3>

                <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                  {article.excerpt}
                </p>

                <div className="flex flex-wrap gap-2 mb-4">
                  {article.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div className="flex items-center gap-1 text-sm text-gray-600">
                    <Icon name="HandThumbUpIcon" size={16} />
                    <span>{article.helpful} found helpful</span>
                  </div>
                  <Icon
                    name="ArrowRightIcon"
                    size={16}
                    className="text-[#42AF47] group-hover:translate-x-1 transition-transform"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
